import { useEffect } from 'react';
import { Link } from 'react-router-dom';

// Public landing page for logged-out, un-invited visitors while the soft-launch
// invite gate is on. Self-contained: no API calls, no listings, prices, stats or
// testimonials. Everything stated here mirrors the Terms of Service / Refund Policy.

export const SUPPORT_EMAIL = 'support@cherrypkd.com';
const INVITE_REQUEST_HREF =
  `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('CherryPKD invite request')}`;
const PAGE_TITLE = 'CherryPKD (Cherry Pickd) — Peer-to-peer collectibles marketplace';
const PAGE_DESC =
  'CherryPKD (Cherry Pickd) is a peer-to-peer marketplace to buy, sell, bid on and rent collectibles. ' +
  'Payments are processed securely by Stripe. Shipped orders are held until delivery and released 3 days after delivery unless a dispute is opened (local pickup: held until the buyer confirms pickup).';

const LEGAL_LINKS = [
  ['Terms of Service', '/terms'],
  ['Privacy Policy', '/privacy'],
  ['Refund Policy', '/refunds'],
];

const STYLES = `
  .pl-root { background: var(--bg); color: var(--text-primary); min-height: 100dvh; display: flex; flex-direction: column; }
  .pl-wrap { max-width: 1120px; margin: 0 auto; padding: 0 clamp(16px, 4vw, 48px); width: 100%; }
  .pl-header { background: #1A1A1A; border-bottom: 1px solid rgba(255,255,255,.08); }
  .pl-header-inner { display: flex; align-items: center; justify-content: space-between; gap: 12px; height: 64px; }
  .pl-logo { display: flex; align-items: center; gap: 8px; text-decoration: none; }
  .pl-logo-word { font-family: 'Playfair Display', serif; font-weight: 700; font-size: 21px; }
  .pl-hero { position: relative; overflow: hidden; background: #1A1A1A; color: #fff; padding: clamp(56px, 9vw, 104px) 0 clamp(56px, 8vw, 96px); }
  .pl-hero-glow { position: absolute; inset: 0; pointer-events: none;
    background: radial-gradient(ellipse at 30% 40%, rgba(192,57,43,.28) 0%, transparent 55%), radial-gradient(ellipse at 75% 60%, rgba(192,57,43,.14) 0%, transparent 50%); }
  .pl-eyebrow { font-size: 12px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: #E74C3C; margin-bottom: 14px; }
  .pl-h1 { font-family: 'Playfair Display', serif; font-weight: 900; font-size: clamp(2.1rem, 5.5vw, 3.4rem); line-height: 1.1; margin-bottom: 18px; }
  .pl-lead { font-size: clamp(15px, 2vw, 18px); color: rgba(255,255,255,.72); line-height: 1.65; max-width: 640px; margin-bottom: 30px; }
  .pl-cta-row { display: flex; gap: 12px; flex-wrap: wrap; align-items: center; }
  .pl-btn-light { background: transparent; color: #fff; border: 1.5px solid rgba(255,255,255,.35); }
  .pl-btn-light:hover, .pl-btn-light:focus-visible { background: #fff; color: #1A1A1A; border-color: #fff; }
  .pl-trust { margin-top: 26px; display: flex; gap: 10px; flex-wrap: wrap; }
  .pl-chip { padding: 6px 12px; border-radius: 100px; background: rgba(255,255,255,.08); font-size: 12.5px; font-weight: 600; color: rgba(255,255,255,.78); }
  .pl-section { padding: clamp(48px, 7vw, 80px) 0; }
  .pl-section-alt { background: var(--cream); }
  .pl-h2 { font-family: 'Playfair Display', serif; font-weight: 700; font-size: clamp(1.6rem, 3.4vw, 2.2rem); line-height: 1.2; margin-bottom: 10px; }
  .pl-sub { font-size: 15px; color: var(--text-mid); line-height: 1.65; max-width: 680px; margin-bottom: 32px; }
  .pl-grid4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
  .pl-grid2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }
  .pl-card { background: var(--white); border: 1px solid var(--border); border-radius: 16px; padding: 22px; box-shadow: var(--shadow-sm); }
  .pl-card-icon { font-size: 28px; margin-bottom: 10px; }
  .pl-card h3 { font-size: 16px; font-weight: 700; margin-bottom: 6px; }
  .pl-card p { font-size: 14px; color: var(--text-mid); line-height: 1.6; }
  .pl-steps { list-style: none; display: flex; flex-direction: column; gap: 14px; margin-top: 14px; }
  .pl-steps li { display: flex; gap: 12px; font-size: 14px; color: var(--text-mid); line-height: 1.6; }
  .pl-step-n { flex-shrink: 0; width: 26px; height: 26px; border-radius: 50%; background: var(--cherry-light); color: var(--cherry); font-weight: 700; font-size: 13px; display: flex; align-items: center; justify-content: center; }
  .pl-steps strong { color: var(--text-primary); }
  .pl-link { color: var(--cherry); font-weight: 600; text-decoration: none; }
  .pl-link:hover, .pl-link:focus-visible { text-decoration: underline; }
  .pl-support { text-align: center; }
  .pl-support .pl-sub { margin-left: auto; margin-right: auto; }
  /* Prod smoke #12: existing members can always reach the login page; 40px tap area. */
  .pl-header-actions { display: flex; align-items: center; gap: 8px; }
  .pl-login-link { display: inline-flex; align-items: center; justify-content: center; min-height: 40px; min-width: 40px; padding: 0 12px; box-sizing: border-box; color: #fff; font-weight: 600; font-size: 14px; text-decoration: none; border-radius: 8px; }
  .pl-login-link:hover, .pl-login-link:focus-visible { background: rgba(255,255,255,.1); text-decoration: underline; }
  /* UX review: larger helper text; rgba(255,255,255,.82) is >= 8.71:1 on the hero (#1A1A1A + red glow). */
  .pl-login-line { margin-top: 18px; font-size: 15px; color: rgba(255,255,255,.82); }
  .pl-invite-email { font-size: 15px; color: rgba(255,255,255,.82); }
  .pl-invite-email strong { color: #fff; font-weight: 600; user-select: all; overflow-wrap: anywhere; }
  .pl-login-line .pl-login-link { padding: 0 6px; text-decoration: underline; }
  /* UX review: #AAAAAA on #2C2C2C is 6.01:1 (was #888, 3.94:1); links stay #aaa (6.01:1). */
  .pl-footer { background: #2C2C2C; color: #AAAAAA; padding: 32px 0 28px; margin-top: auto; }
  .pl-footer-inner { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; font-size: 13px; }
  .pl-footer-links { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  /* QA r6: 40px tap area like the gate/login footer (text size unchanged; 8px gap + 5px side padding keeps the old 18px spacing). */
  .pl-footer-links a { display: inline-flex; align-items: center; justify-content: center; min-height: 40px; min-width: 40px; padding: 0 5px; box-sizing: border-box; }
  .pl-footer a { color: #aaa; text-decoration: none; }
  .pl-footer a:hover, .pl-footer a:focus-visible { color: #fff; }
  @media (max-width: 900px) { .pl-grid4 { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 640px) {
    .pl-grid4, .pl-grid2 { grid-template-columns: 1fr; }
    .pl-header-cta-text { display: none; }
  }
`;

function Logo() {
  return (
    <Link to="/" className="pl-logo" aria-label="CherryPKD home">
      <span style={{ fontSize: 22 }} aria-hidden>🍒</span>
      <span className="pl-logo-word">
        <span style={{ color: '#E74C3C' }}>Cherry</span><span style={{ color: '#fff' }}>PKD</span>
      </span>
    </Link>
  );
}

function PublicHeader({ onEnterInvite }) {
  return (
    <header className="pl-header">
      <div className="pl-wrap pl-header-inner">
        <Logo />
        <div className="pl-header-actions">
          <Link to="/login" className="pl-login-link">Log in</Link>
          {onEnterInvite && (
            <button type="button" className="btn btn-cherry btn-sm" onClick={onEnterInvite}>
              🔑 <span className="pl-header-cta-text">Enter invite code</span>
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

export function PublicFooter() {
  return (
    <footer className="pl-footer">
      <div className="pl-wrap pl-footer-inner">
        <span>© {new Date().getFullYear()} CherryPKD (Cherry Pickd). All rights reserved.</span>
        <nav className="pl-footer-links" aria-label="Legal">
          {LEGAL_LINKS.map(([label, path]) => (
            <Link key={path} to={path}>{label}</Link>
          ))}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </nav>
      </div>
    </footer>
  );
}

/**
 * Minimal public chrome (header + footer) used for pages that must stay viewable
 * while the invite gate is on (the landing page and /terms, /privacy, /refunds).
 * Does not render the marketplace Nav, cart, sockets or any marketplace links.
 */
export function PublicPageShell({ onEnterInvite, children }) {
  return (
    <div className="pl-root">
      <style>{STYLES}</style>
      <PublicHeader onEnterInvite={onEnterInvite} />
      <main id="main-content" style={{ flex: 1 }}>{children}</main>
      <PublicFooter />
    </div>
  );
}

const OFFERINGS = [
  { icon: '🛍️', title: 'Buy', text: 'Buy collectibles directly from other collectors and sellers in the community.' },
  { icon: '🏷️', title: 'Sell', text: 'List your collectibles with photos and an honest description, and sell to buyers on CherryPKD.' },
  { icon: '🔨', title: 'Bid', text: 'Join auctions. Bids are binding, and the highest valid bid when the auction closes wins.' },
  { icon: '📅', title: 'Rent', text: 'Rent items from their owners for a set period, then return them in the same condition.' },
];

export default function PublicLandingPage({ onEnterInvite }) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = PAGE_TITLE;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute('content') : null;
    if (meta) meta.setAttribute('content', PAGE_DESC);
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc !== null) meta.setAttribute('content', prevDesc);
    };
  }, []);

  return (
    <PublicPageShell onEnterInvite={onEnterInvite}>
      {/* Hero */}
      <section className="pl-hero" aria-labelledby="pl-hero-title">
        <div className="pl-hero-glow" aria-hidden />
        <div className="pl-wrap" style={{ position: 'relative', zIndex: 1 }}>
          <div className="pl-eyebrow">Soft launch · Invite only</div>
          <h1 id="pl-hero-title" className="pl-h1">CherryPKD (Cherry Pickd)</h1>
          <p className="pl-lead">
            A peer-to-peer marketplace to buy, sell, bid on and rent collectibles. Payments are
            processed by Stripe and held while each order is completed.
          </p>
          <div className="pl-cta-row">
            {onEnterInvite && (
              <button type="button" className="btn btn-cherry btn-lg" onClick={onEnterInvite}>
                Enter invite code
              </button>
            )}
            <a className="btn btn-lg pl-btn-light" href={INVITE_REQUEST_HREF}>
              Request an invite
            </a>
            <span className="pl-invite-email">or email <strong>{SUPPORT_EMAIL}</strong></span>
          </div>
          <p className="pl-login-line">
            Already have an account? <Link to="/login" className="pl-login-link">Log in</Link>
          </p>
          <div className="pl-trust">
            <span className="pl-chip">🔒 Payments processed by Stripe</span>
            <span className="pl-chip">🛡️ Funds held until the order is confirmed</span>
            <span className="pl-chip">🏷️ 8% seller platform fee (min $1.00 per order)</span>
          </div>
        </div>
      </section>

      {/* What you can do */}
      <section className="pl-section" aria-labelledby="pl-what">
        <div className="pl-wrap">
          <h2 id="pl-what" className="pl-h2">What is CherryPKD?</h2>
          <p className="pl-sub">
            CherryPKD connects people who own collectibles with people who want them. Members transact
            directly with each other; CherryPKD runs the marketplace, processes payments through Stripe
            and holds funds while each order is completed.
          </p>
          <div className="pl-grid4">
            {OFFERINGS.map((o) => (
              <div key={o.title} className="pl-card">
                <div className="pl-card-icon" aria-hidden>{o.icon}</div>
                <h3>{o.title}</h3>
                <p>{o.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="pl-section pl-section-alt" aria-labelledby="pl-how">
        <div className="pl-wrap">
          <h2 id="pl-how" className="pl-h2">How it works</h2>
          <p className="pl-sub">Every purchase follows the same protected flow, whether you are buying or selling.</p>
          <div className="pl-grid2">
            <div className="pl-card">
              <h3>🛒 For buyers</h3>
              <ol className="pl-steps">
                <li><span className="pl-step-n">1</span><span><strong>Find an item.</strong> Buy it outright, bid on it in an auction, or rent it from its owner.</span></li>
                <li><span className="pl-step-n">2</span><span><strong>Pay securely.</strong> Checkout payments are processed by Stripe. CherryPKD does not store full card details.</span></li>
                <li><span className="pl-step-n">3</span><span><strong>Payment is held.</strong> For shipped orders, your payment is held until delivery and released to the seller 3 days after delivery unless you open a dispute. For local pickup, it is held until you confirm the pickup is completed.</span></li>
                <li><span className="pl-step-n">4</span><span><strong>Something wrong?</strong> Open a dispute while the payment is held. Approved refunds go back to your original payment method through Stripe.</span></li>
              </ol>
            </div>
            <div className="pl-card">
              <h3>🏷️ For sellers</h3>
              <ol className="pl-steps">
                <li><span className="pl-step-n">1</span><span><strong>List your item.</strong> Add clear photos of the actual item and an accurate description, including condition and any defects.</span></li>
                <li><span className="pl-step-n">2</span><span><strong>Make the sale.</strong> Sell at a fixed price, run an auction, or offer it for rent.</span></li>
                <li><span className="pl-step-n">3</span><span><strong>Hand it over.</strong> Ship the item or meet the buyer for pickup.</span></li>
                <li><span className="pl-step-n">4</span><span><strong>Get paid.</strong> Shipped orders are released to you 3 days after delivery unless the buyer opens a dispute; local pickups are released once the buyer confirms the pickup is completed. The 8% platform fee is deducted from your payout.</span></li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* Payments & fees */}
      <section className="pl-section" aria-labelledby="pl-pay">
        <div className="pl-wrap">
          <h2 id="pl-pay" className="pl-h2">Payments, fees and refunds</h2>
          <div className="pl-grid2" style={{ marginTop: 24 }}>
            <div className="pl-card">
              <div className="pl-card-icon" aria-hidden>🔒</div>
              <h3>Secure payments by Stripe</h3>
              <p>
                All payments on CherryPKD are processed securely by Stripe. For shipped orders, funds are held
                until delivery and released to the seller 3 days after delivery unless a dispute is opened. For
                local pickup, funds are held until the buyer confirms pickup. See
                our <Link className="pl-link" to="/terms">Terms of Service</Link>.
              </p>
            </div>
            <div className="pl-card">
              <div className="pl-card-icon" aria-hidden>🏷️</div>
              <h3>8% platform fee for sellers (min $1.00 per order)</h3>
              <p>
                Sellers pay a platform fee of 8% (minimum $1.00 per order, never more than the item price), deducted automatically from the payout. Buyers pay
                the listed price plus shipping, with no buyer fee. Refunds and disputes are covered in
                our <Link className="pl-link" to="/refunds">Refund Policy</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Invite + support */}
      <section className="pl-section pl-section-alt pl-support" aria-labelledby="pl-support">
        <div className="pl-wrap">
          <h2 id="pl-support" className="pl-h2">Get access and get help</h2>
          <p className="pl-sub">
            CherryPKD is in an invite-only soft launch. Have a code? Enter it to come in. Need one, or have a
            question about an order, payment or refund? Email us
            at <a className="pl-link" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </p>
          <div className="pl-cta-row" style={{ justifyContent: 'center' }}>
            {onEnterInvite && (
              <button type="button" className="btn btn-cherry btn-lg" onClick={onEnterInvite}>
                Enter invite code
              </button>
            )}
            <a className="btn btn-ghost btn-lg" href={INVITE_REQUEST_HREF}>Request an invite</a>
          </div>
        </div>
      </section>
    </PublicPageShell>
  );
}
